import type { IUserInfo } from "@/account/account-models";
import { EUserType } from "@/account/account-models";
import type { IChatFilter } from "@/chats/chats-helpers";
import { EChatFilterCategory, EChatFilterType } from "@/chats/chats-helpers";
import type { IDictionary } from "@/models";
import { defined } from "@/utils/variable-evaluation";

export enum EBadgeType {
  // -- DISCOVER --
  New = "New",
  Free = "Free",
  // -- PROFILE --
  Vip = "Vip",
  Staff = "Staff",
  NoCredits = "NoCredits",
  PaidFan = "PaidFan",
  Fan = "Fan",
  Bonus = "Bonus",
  Subscriber = "Subscriber",
}

export function getChatsListBadges(
  myUser: IUserInfo,
  otherUser: IUserInfo,
  chatFiltersSet: IDictionary<IChatFilter>
): EBadgeType[] {
  const list: EBadgeType[] = [];
  if (myUser.type === EUserType.Featured) {
    // Staff - FMs will see this badge on an FM with is_staff or is_staff_mo.
    if (
      otherUser.type === EUserType.Featured &&
      (otherUser.is_staff || otherUser.is_staff_mo)
    ) {
      return [EBadgeType.Staff]; // only Staff badge and nothing else
    }

    const isFansFilter =
      chatFiltersSet[EChatFilterType.Category].value ===
      EChatFilterCategory.Fans;
    if (
      isFansFilter &&
      otherUser.type === EUserType.User &&
      otherUser.affiliate === myUser._id &&
      otherUser.is_customer
    ) {
      // PAID - User signed up under the model and already bought some package.
      list.push(EBadgeType.PaidFan);
    }
    if (
      otherUser.type === EUserType.User &&
      otherUser.affiliate === myUser._id
    ) {
      // FAN - User signed up under the model.
      list.push(EBadgeType.Fan);
    }
    if (
      otherUser.type === EUserType.User &&
      otherUser.affiliate !== myUser._id
    ) {
      // BONUS - Any user who did not sign up under the model.
      list.push(EBadgeType.Bonus);
    }
    if (otherUser.type === EUserType.User && otherUser.is_subscriber) {
      // Subscriber - Any active subscriber, badge can be removed as soon as the user cancels his subscription.
      list.push(EBadgeType.Subscriber);
    }
    if (otherUser.type === EUserType.User && otherUser.is_vip) {
      // VIP - FMs will see this badge on a user with is_vip.
      list.push(EBadgeType.Vip);
    }
    // if (otherUser.type === EUserType.User && otherUser.credits <= 0) {
    //   // No Credits - FMs will see this badge on a user with 0 Credits.
    //   list.push(EBadge.NoCredits);
    // }
  } else {
    // my user is EUserType.User
    if (otherUser.type === EUserType.Featured && otherUser.is_staff) {
      // Staff - users will see this badge on an FM with is_staff.
      return [EBadgeType.Staff]; // only Staff badge and nothing else
    }
  }

  return list;
}

export function getChatHeaderBadgesList(
  myUser: IUserInfo,
  otherUser: IUserInfo,
  width: number
): EBadgeType[] {
  // In chat screen show badges under "Name" and followed by "online status".
  const list: EBadgeType[] = [];
  if (myUser.type === EUserType.Featured) {
    // Staff - FMs will see this badge on an FM with is_staff or is_staff_mo.
    if (
      otherUser.type === EUserType.Featured &&
      (otherUser.is_staff || otherUser.is_staff_mo)
    ) {
      return [EBadgeType.Staff]; // only Staff badge and nothing else
    }

    if (
      otherUser.type === EUserType.User &&
      otherUser.affiliate === myUser._id
    ) {
      // FAN - User signed up under the model.
      list.push(EBadgeType.Fan);
    }
    if (
      otherUser.type === EUserType.User &&
      otherUser.affiliate !== myUser._id
    ) {
      // BONUS - Any user who did not sign up under the model.
      list.push(EBadgeType.Bonus);
    }
    // if (otherUser.type === EUserType.User && otherUser.is_subscriber) {
    //   // Subscriber - Any active subscriber, badge can be removed as soon as the user cancels his subscription.
    //   list.push(EBadge.Subscriber);
    // }
    // if (otherUser.type === EUserType.User && otherUser.is_vip) {
    //   // VIP - FMs will see this badge on a user with is_vip.
    //   list.push(EBadge.Vip);
    // }
    if (otherUser.type === EUserType.User && otherUser.credits <= 0) {
      // No Credits - FMs will see this badge on a user with 0 Credits.
      list.push(EBadgeType.NoCredits);
    }
  } else {
    // my user is EUserType.User
    if (otherUser.type === EUserType.Featured && otherUser.is_staff) {
      // Staff - users will see this badge on an FM with is_staff.
      return [EBadgeType.Staff]; // only Staff badge and nothing else
    }
  }

  return list;
}

export function getProfileBadgesList(
  myUser: IUserInfo,
  otherUser: IUserInfo
): EBadgeType[] {
  if (!defined(myUser)) {
    return [];
  }

  // In chat screen show badges under "Name" and followed by "online status".
  const list: EBadgeType[] = [];
  if (myUser.type === EUserType.Featured) {
    // Staff - FMs will see this badge on an FM with is_staff or is_staff_mo.
    if (
      otherUser.type === EUserType.Featured &&
      (otherUser.is_staff || otherUser.is_staff_mo)
    ) {
      return [EBadgeType.Staff]; // only Staff badge and nothing else
    }

    if (
      otherUser.type === EUserType.User &&
      otherUser.affiliate === myUser._id
    ) {
      // FAN - User signed up under the model.
      list.push(EBadgeType.Fan);
    }
    if (
      otherUser.type === EUserType.User &&
      otherUser.affiliate !== myUser._id
    ) {
      // BONUS - Any user who did not sign up under the model.
      list.push(EBadgeType.Bonus);
    }
    if (otherUser.type === EUserType.User && otherUser.is_subscriber) {
      // Subscriber - Any active subscriber, badge can be removed as soon as the user cancels his subscription.
      list.push(EBadgeType.Subscriber);
    }
    if (otherUser.type === EUserType.User && otherUser.is_vip) {
      // VIP - FMs will see this badge on a user with is_vip.
      list.push(EBadgeType.Vip);
    }
    // if (otherUser.type === EUserType.User && otherUser.credits <= 0) {
    //   // No Credits - FMs will see this badge on a user with 0 Credits.
    //   list.push(EBadge.NoCredits);
    // }
  } else {
    // my user is EUserType.User
    if (otherUser.type === EUserType.Featured && otherUser.is_staff) {
      // Staff - users will see this badge on an FM with is_staff.
      return [EBadgeType.Staff]; // only Staff badge and nothing else
    }
    if (otherUser.type === EUserType.User && otherUser.is_vip) {
      // VIP - user will see this badge on a user with is_vip.
      list.push(EBadgeType.Vip);
    }
  }

  return list;
}
